/**
 * inbox-account.ts — Hộp thư (Nhắn tin) NHỚ tài khoản kênh đang chọn + chấm báo tin chưa đọc ở CÁC TÀI KHOẢN KHÁC.
 *
 * Góp ý 13/10/2026 (Julie · EvoTech): chọn một tài khoản để chăm sóc khách, bấm sang mục khác rồi quay lại thì Nhắn tin về "Tất cả tài khoản" gây rối;
 * muốn giữ nguyên tài khoản đã chọn, còn tin của các kênh khác thì có chấm báo để nhân viên tự chuyển.
 *  - Nhớ theo từng người dùng trên máy (localStorage `ecdp_inbox_account:<userId>`): chuyển mục, tải lại trang, mở lại trình duyệt vẫn giữ; chọn "Tất cả" thì xoá.
 *  - Tài khoản đã nhớ mà không còn (xoá / mất quyền) → về "Tất cả" (khỏi kẹt ở danh sách rỗng).
 *  - Số chưa đọc theo tài khoản do backend trả trong `GET /conversations/counts` (`unreadByAccount`, modules/chat/unread-by-account.ts).
 * Hàm thuần — trang gọi: pages/conversations/conversations-page.tsx, filter-rail.tsx (máy tính), mobile-inbox-header.tsx (điện thoại).
 */

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export const inboxAccountKey = (userId?: string | null) => `ecdp_inbox_account:${userId || 'anon'}`

/** Đọc tài khoản đã nhớ (chưa có / bộ nhớ bị chặn → undefined = "Tất cả"). */
export function loadInboxAccount(userId: string | null | undefined, store?: KeyValueStore): string | undefined {
  try {
    const v = (store ?? localStorage).getItem(inboxAccountKey(userId))
    return v && v.length <= 64 ? v : undefined
  } catch {
    return undefined
  }
}

/** Nhớ tài khoản đang chọn; `undefined` ("Tất cả") = xoá bản nhớ. */
export function saveInboxAccount(userId: string | null | undefined, accountId: string | undefined, store?: KeyValueStore): void {
  try {
    const s = store ?? localStorage
    if (accountId) s.setItem(inboxAccountKey(userId), accountId)
    else s.removeItem(inboxAccountKey(userId))
  } catch { /* bộ nhớ bị chặn / đầy → vẫn dùng được trong phiên */ }
}

/**
 * Tài khoản đã nhớ còn dùng được không. Danh sách tài khoản CHƯA tải (undefined) → giữ nguyên (chờ); đã tải mà không có mã đó → undefined ("Tất cả").
 */
export function resolveSavedAccount(saved: string | undefined, accounts: { id: string }[] | undefined): string | undefined {
  if (!saved) return undefined
  if (!accounts) return saved
  return accounts.some((a) => a.id === saved) ? saved : undefined
}

/**
 * Số hội thoại chưa đọc của tài khoản `id` khi người dùng đang xem MỘT tài khoản khác (`selectedId`). Đang xem "Tất cả" hoặc chính tài khoản đó → 0
 * (danh sách đã hiện hết; chấm báo chỉ để nhắc các kênh đang bị bỏ ngoài bộ lọc).
 */
export function otherAccountUnread(unreadByAccount: Record<string, number> | undefined, selectedId: string | undefined, id: string): number {
  if (!selectedId || id === selectedId) return 0
  const n = unreadByAccount?.[id]
  return typeof n === 'number' && n > 0 ? n : 0
}

/** Tổng chưa đọc ở mọi tài khoản KHÁC tài khoản đang chọn (dấu báo trên nút chọn tài khoản). Không chọn tài khoản nào → 0. */
export function othersUnreadTotal(unreadByAccount: Record<string, number> | undefined, selectedId: string | undefined): number {
  if (!selectedId || !unreadByAccount) return 0
  let sum = 0
  for (const [id, n] of Object.entries(unreadByAccount)) if (id !== selectedId && typeof n === 'number' && n > 0) sum += n
  return sum
}

/** Số hiển thị trên huy hiệu, rút gọn 99+. */
export const shortUnreadCount = (n: number): string => (n > 99 ? '99+' : String(n))
